import { Schema } from "effect";
import { ShopperAiIntegrationId } from "../../../ids/definitions";

// Individual integration type literals derived from ShopperAiIntegrationType
export const GmailIntegrationType = Schema.Literal("gmail");
export const OutlookIntegrationType = Schema.Literal("outlook");
export const FacebookIntegrationType = Schema.Literal("facebook");
export const InstagramIntegrationType = Schema.Literal("instagram");
export const AttentiveIntegrationType = Schema.Literal("attentive");
export const PostscriptIntegrationType = Schema.Literal("postscript");
export const ImessageIntegrationType = Schema.Literal("imessage");
export const RcsIntegrationType = Schema.Literal("rcs");
export const GorgiasSmsIntegrationType = Schema.Literal("gorgias_sms");
export const SharedSmsIntegrationType = Schema.Literal("shared_sms");
export const RichpanelSmsIntegrationType = Schema.Literal("richpanel_sms");
export const SmsPhoneNumberIntegrationType = Schema.Literal("sms_phone_number");
export const TelnyxSmsIntegrationType = Schema.Literal("telnyx_sms");
export const TelnyxVoiceIntegrationType = Schema.Literal("telnyx_voice");
export const MobileMessageIntegrationType = Schema.Literal("mobileMessage");
export const TiktokIntegrationType = Schema.Literal("tiktok");
export const RedoChatAiIntegrationType = Schema.Literal("redoChatAi");
export const RedoChatFlowIntegrationType = Schema.Literal("redoChatFlow");
export const RedoChatLiveIntegrationType = Schema.Literal("redoChatLive");
export const ContactFormIntegrationType = Schema.Literal("contactForm");
export const CustomEmailIntegrationType = Schema.Literal("custom_email");

// Gmail channel data (excluding refreshToken)
export const GmailChannelData = Schema.Struct({
  email: Schema.String,
  emailSignature: Schema.NullOr(Schema.String),
  gmailAccountName: Schema.NullOr(Schema.String),
  gmailAccountImage: Schema.NullOr(Schema.String),
});
export type GmailChannelData = typeof GmailChannelData.Type;

// Outlook channel data (excluding refreshToken)
export const OutlookChannelData = Schema.Struct({
  email: Schema.String,
  emailSignature: Schema.NullOr(Schema.String),
  name: Schema.NullOr(Schema.String),
});
export type OutlookChannelData = typeof OutlookChannelData.Type;

// Facebook channel data (excluding accessToken, appScopedUserId)
export const FacebookChannelData = Schema.Struct({
  pageId: Schema.String,
  pageName: Schema.String,
  facebookProfilePicUrl: Schema.NullOr(Schema.String),
  permissions: Schema.NullOr(Schema.Array(Schema.String)),
});
export type FacebookChannelData = typeof FacebookChannelData.Type;

// Instagram channel data (excluding accessToken, appScopedUserId)
export const InstagramChannelData = Schema.Struct({
  pageId: Schema.String,
  pageName: Schema.String,
  instagramBusinessAccountId: Schema.String,
  instagramProfilePicUrl: Schema.NullOr(Schema.String),
});
export type InstagramChannelData = typeof InstagramChannelData.Type;

// Attentive channel data (excluding accessToken, accessTokenExpireTime)
export const AttentiveChannelData = Schema.Struct({
  attentiveCompanyId: Schema.NullOr(Schema.String),
  attentiveCompanyName: Schema.NullOr(Schema.String),
  purposes: Schema.NullOr(Schema.Array(Schema.String)),
});
export type AttentiveChannelData = typeof AttentiveChannelData.Type;

// Postscript channel data (excluding apiKey)
export const PostscriptChannelData = Schema.Struct({
  shopId: Schema.NullOr(Schema.String),
});
export type PostscriptChannelData = typeof PostscriptChannelData.Type;

// TikTok channel data (excluding accessToken, refreshToken, accessTokenExpireTime, refreshTokenExpireTime)
export const TiktokChannelData = Schema.Struct({
  shopId: Schema.String,
  sellerName: Schema.String,
  sellerRegion: Schema.String,
});
export type TiktokChannelData = typeof TiktokChannelData.Type;

// Redo Chat AI channel data (no specific data needed)
export const RedoChatAiChannelData = Schema.Struct({});
export type RedoChatAiChannelData = typeof RedoChatAiChannelData.Type;

// Redo Chat Flow channel data (no specific data needed)
export const RedoChatFlowChannelData = Schema.Struct({});
export type RedoChatFlowChannelData = typeof RedoChatFlowChannelData.Type;

// Redo Chat Live channel data (no specific data needed)
export const RedoChatLiveChannelData = Schema.Struct({});
export type RedoChatLiveChannelData = typeof RedoChatLiveChannelData.Type;

// SMS phone number channel data (for marketing and transactional usecases)
export const SmsPhoneNumberChannelData = Schema.Struct({});
export type SmsPhoneNumberChannelData = typeof SmsPhoneNumberChannelData.Type;

// iMessage channel data
export const ImessageChannelData = Schema.Struct({});
export type ImessageChannelData = typeof ImessageChannelData.Type;

// RCS channel data. Like imessage, the integration is per-account and carries no
// channel-specific fields; the dedicated RCS number lives on the phone doc.
export const RcsChannelData = Schema.Struct({});
export type RcsChannelData = typeof RcsChannelData.Type;

// Gorgias SMS channel data. The integration is a per-account mirror of the
// merchant's Gorgias helpdesk: its threads archive conversations Gorgias owns
// (escalated tickets resolved by Gorgias agents) — Redo cannot send on them.
export const GorgiasSmsChannelData = Schema.Struct({});
export type GorgiasSmsChannelData = typeof GorgiasSmsChannelData.Type;

// Shared-SMS channel data. Per-account integration for the shared onboarding
// number (no per-team phone doc), keyed by account like imessage.
export const SharedSmsChannelData = Schema.Struct({});
export type SharedSmsChannelData = typeof SharedSmsChannelData.Type;
// Richpanel SMS channel data. Like Gorgias, this is a per-account mirror of the
// merchant's Richpanel helpdesk: its threads archive conversations Richpanel
// owns (escalated tickets resolved by Richpanel agents) — Redo cannot send on them.
export const RichpanelSmsChannelData = Schema.Struct({});
export type RichpanelSmsChannelData = typeof RichpanelSmsChannelData.Type;

// Telnyx SMS channel data
export const TelnyxSmsChannelData = Schema.Struct({
  phoneNumber: Schema.String,
  telnyxPhoneNumberId: Schema.NullOr(Schema.String),
  messagingProfileId: Schema.NullOr(Schema.String),
});
export type TelnyxSmsChannelData = typeof TelnyxSmsChannelData.Type;

// Telnyx Voice channel data — Telnyx resources provisioned by
// `setupTeamVoiceResources`. These drive per-agent credential minting in
// `UserCredentialService`; they are team-scoped, not per-phone-number.
export const TelnyxVoiceChannelData = Schema.Struct({
  sipConnectionId: Schema.NullOr(Schema.String),
  sipConnectionCredential: Schema.NullOr(Schema.String),
  outboundVoiceProfileId: Schema.NullOr(Schema.String),
  voiceApplicationId: Schema.NullOr(Schema.String),
  billingGroupId: Schema.NullOr(Schema.String),
});
export type TelnyxVoiceChannelData = typeof TelnyxVoiceChannelData.Type;

// Mobile Message channel data (legacy phone-gateway messages). No integration
// payload is needed; the lane stores the customer/merchant phone pair.
export const MobileMessageChannelData = Schema.Struct({});
export type MobileMessageChannelData = typeof MobileMessageChannelData.Type;

// Contact form channel data — tracks the default email and SMS integrations
// that merchants reply FROM when a contact form submission comes in.
export const ContactFormIntegrationChannelData = Schema.Struct({
  emailIntegrationId: Schema.NullOr(ShopperAiIntegrationId),
  smsIntegrationId: Schema.NullOr(ShopperAiIntegrationId),
});
export type ContactFormIntegrationChannelData =
  typeof ContactFormIntegrationChannelData.Type;

// Custom email channel data — Postmark-backed merchant email inbox. Inbound
// is delivered through Postmark's inbound webhook; the integration row
// captures the merchant's verified sender signature plus the
// `receiveEmail` redirect address that customers reply to.
export const CustomEmailChannelData = Schema.Struct({
  email: Schema.String,
  name: Schema.String,
  postmarkId: Schema.Number,
  domainId: Schema.Number,
  dkimHost: Schema.String,
  dkimText: Schema.String,
  returnPathDomain: Schema.String,
  returnPathValue: Schema.NullOr(Schema.String),
  setupStatus: Schema.String,
  receiveEmail: Schema.NullOr(Schema.String),
});
export type CustomEmailChannelData = typeof CustomEmailChannelData.Type;
